import type { ReportColumn } from '../models/column.types'
import type { ReportRow } from '../models/report.types'

import { calcChange } from './calcChange'
import type { ReportTotalSources } from './tablePresentation'

export interface ComparisonColumnLabels {
  previous: (column: ReportColumn) => string
  change: (column: ReportColumn) => string
}

function hasValue(value: unknown): boolean {
  return value !== undefined && value !== null && value !== ''
}

function nestedRows(row: ReportRow): ReportRow[] {
  return [
    ...(Array.isArray(row.edges) ? row.edges : []),
    ...(Array.isArray(row.breakdown_values) ? row.breakdown_values : []),
  ]
}

function rowsHavePreviousValue(rows: readonly ReportRow[], field: string): boolean {
  return rows.some(
    (row) => hasValue(row[`prev_${field}`]) || rowsHavePreviousValue(nestedRows(row), field),
  )
}

/** Visible numeric base fields for which the settled response contains comparison data. */
export function getExpandableComparisonFields(
  columns: readonly ReportColumn[],
  rows: readonly ReportRow[],
): string[] {
  return columns
    .filter(
      (column) => column.numeric && !column.invisible && rowsHavePreviousValue(rows, column.field),
    )
    .map((column) => column.field)
}

function comparisonMeta(
  column: ReportColumn,
  kind: 'previous' | 'change',
): Record<string, unknown> {
  return {
    ...(column.meta ?? {}),
    reportComparison: { baseField: column.field, kind },
  }
}

function comparisonColumnKind(column: ReportColumn): 'previous' | 'change' | null {
  const comparison = column.meta?.['reportComparison']
  if (typeof comparison !== 'object' || comparison === null) return null
  const kind = (comparison as Record<string, unknown>)['kind']
  return kind === 'previous' || kind === 'change' ? kind : null
}

function comparisonBaseField(column: ReportColumn): string | null {
  const comparison = column.meta?.['reportComparison']
  if (typeof comparison !== 'object' || comparison === null) return null
  const field = (comparison as Record<string, unknown>)['baseField']
  return typeof field === 'string' && field.length > 0 ? field : null
}

/** True for either render-only column (previous or change) paired with an expanded base metric. */
export function isComparisonColumn(column: ReportColumn): boolean {
  return comparisonColumnKind(column) !== null && comparisonBaseField(column) !== null
}

/** True only for the render-only delta column paired with an expanded base metric. */
export function isComparisonChangeColumn(column: ReportColumn): boolean {
  return comparisonColumnKind(column) === 'change' && comparisonBaseField(column) !== null
}

/** Active comparison base fields encoded by the render-only column snapshot. */
export function getRenderedComparisonFields(columns: readonly ReportColumn[]): string[] {
  return [
    ...new Set(
      columns.flatMap((column) => {
        if (!isComparisonChangeColumn(column)) return []
        const field = comparisonBaseField(column)
        return field ? [field] : []
      }),
    ),
  ]
}

function previousColumn(column: ReportColumn, labels: ComparisonColumnLabels): ReportColumn {
  return {
    ...column,
    field: `prev_${column.field}`,
    header: labels.previous(column),
    invisible: false,
    sortable: false,
    resizable: false,
    meta: comparisonMeta(column, 'previous'),
  }
}

function changeColumn(column: ReportColumn, labels: ComparisonColumnLabels): ReportColumn {
  const result: ReportColumn = { ...column }
  delete result.currency
  delete result.date_col
  delete result.functionType
  delete result.headerImage
  delete result.isImage
  return {
    ...result,
    field: `${column.field}_change`,
    header: labels.change(column),
    sub_type: 'percentage',
    numeric: true,
    percentage: true,
    invisible: false,
    sortable: false,
    resizable: false,
    meta: comparisonMeta(column, 'change'),
  }
}

/** Inserts render-only comparison columns directly after their persisted base column. */
export function buildExpandedComparisonColumns(
  columns: readonly ReportColumn[],
  rows: readonly ReportRow[],
  expandedFields: readonly string[],
  labels: ComparisonColumnLabels,
): ReportColumn[] {
  const expandable = new Set(getExpandableComparisonFields(columns, rows))
  const expanded = new Set(expandedFields)
  return columns.flatMap((column) =>
    expanded.has(column.field) && expandable.has(column.field)
      ? [column, previousColumn(column, labels), changeColumn(column, labels)]
      : [column],
  )
}

export function buildComparisonRow(row: ReportRow, expandedFields: readonly string[]): ReportRow {
  const next: ReportRow = { ...row }
  for (const field of expandedFields) next[`${field}_change`] = calcChange(row, field)
  if (Array.isArray(row.edges)) next.edges = buildComparisonRows(row.edges, expandedFields)
  if (Array.isArray(row.breakdown_values))
    next.breakdown_values = buildComparisonRows(row.breakdown_values, expandedFields)
  return next
}

/** Adds ratio-valued change cells to an immutable render/export row snapshot. */
export function buildComparisonRows(
  rows: readonly ReportRow[],
  expandedFields: readonly string[],
): ReportRow[] {
  return rows.map((row) => buildComparisonRow(row, expandedFields))
}

/** Derives render-only comparison deltas for every controller total bucket, immutably. */
export function buildComparisonTotalSources(
  sources: ReportTotalSources,
  expandedFields: readonly string[],
): ReportTotalSources {
  function derive(source: Record<string, unknown> | null): ReportRow | null {
    return source ? buildComparisonRow(source, expandedFields) : source
  }

  return {
    ...(sources.total === undefined ? {} : { total: derive(sources.total) }),
    ...(sources.otherTotal === undefined ? {} : { otherTotal: derive(sources.otherTotal) }),
    ...(sources.grandTotal === undefined ? {} : { grandTotal: derive(sources.grandTotal) }),
    ...(sources.ga4Total === undefined ? {} : { ga4Total: derive(sources.ga4Total) }),
  }
}
